import React, { useState, ReactNode } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { BundleWork } from 'js/bulk_download/work_cards_shared/types';
import $image_url from 'js/utils/image_url';

interface Props {
  relatedWorks: BundleWork[];
  visibleRelatedWorks: BundleWork[];
  sourceWorkPreview: BundleWork | undefined;
  includedWorkSlot: ReactNode;
  showAnnotations: boolean;
  badgePlacement: 'side' | 'below' | 'above';
  previewFooter: ReactNode;
  emptyState: ReactNode;
  showBucketBadges: boolean;
  totalRelatedCount: number;
}

function DetailPanel({ work }: { work: BundleWork }) {
  const thumbnailSrc = work.thumbnailUrl
    ? $image_url(work.thumbnailUrl)
    : '/images/blank-paper.jpg';

  return (
    <DetailWrap>
      <DetailThumbnail>
        <img alt="" src={thumbnailSrc} />
      </DetailThumbnail>
      <Body size="md" bold as="h3" style={titleStyle}>
        {work.title}
      </Body>
      {work.authors && (
        <Body size="xs" as="div" style={secondaryText}>
          by {work.authors}
        </Body>
      )}
      <DetailMeta>
        {work.year && (
          <Body size="xs" as="span" style={secondaryText}>
            {work.year}
          </Body>
        )}
        {work.viewCount > 0 && (
          <Body size="xs" as="span" style={secondaryText}>
            {work.viewCount.toLocaleString()}{' '}
            {work.viewCount === 1 ? 'view' : 'views'}
          </Body>
        )}
      </DetailMeta>
      {work.summary && (
        <AbstractSection>
          <Body size="xs" bold as="div" style={secondaryText}>
            Abstract
          </Body>
          <Body size="xs" as="div" style={abstractStyle}>
            {work.summary}
          </Body>
        </AbstractSection>
      )}
    </DetailWrap>
  );
}

export function PaperBrowserPreviewList({
  visibleRelatedWorks,
  sourceWorkPreview,
  previewFooter,
  emptyState,
}: Props) {
  const allWorks = sourceWorkPreview
    ? [sourceWorkPreview, ...visibleRelatedWorks]
    : visibleRelatedWorks;

  const [selectedId, setSelectedId] = useState<number | null>(
    allWorks[0]?.id ?? null,
  );

  const selectedWork = allWorks.find((w) => w.id === selectedId) ?? allWorks[0];

  if (allWorks.length === 0) {
    return <>{emptyState}</>;
  }

  return (
    <BrowserContainer>
      <ListPane>
        <ListScroll>
          {allWorks.map((work, i) => {
            const isSource =
              sourceWorkPreview && work.id === sourceWorkPreview.id;
            const isSelected = work.id === selectedWork?.id;
            return (
              <ListItem
                key={work.id}
                type="button"
                data-selected={isSelected || undefined}
                onClick={() => setSelectedId(work.id)}
              >
                <ListItemNumber>
                  <Body size="xs" as="span" style={secondaryText}>
                    {i + 1}
                  </Body>
                </ListItemNumber>
                <ListItemContent>
                  {isSource && (
                    <Body size="xs" as="div" style={sourceLabel}>
                      Your paper
                    </Body>
                  )}
                  <ListItemTitle>
                    <Body size="xs" bold={isSelected} as="span">
                      {work.title}
                    </Body>
                  </ListItemTitle>
                  {work.authors && (
                    <Body size="xs" as="div" style={secondaryText}>
                      {work.authors}
                    </Body>
                  )}
                </ListItemContent>
              </ListItem>
            );
          })}
        </ListScroll>
        {previewFooter}
      </ListPane>
      <PreviewPane>
        {selectedWork && <DetailPanel work={selectedWork} />}
      </PreviewPane>
    </BrowserContainer>
  );
}

const titleStyle: React.CSSProperties = {
  color: 'var(--text-primary, #141413)',
  margin: 0,
};

const secondaryText: React.CSSProperties = {
  color: 'var(--text-secondary, #535366)',
};

const sourceLabel: React.CSSProperties = {
  color: 'var(--primary-brand, #0645b1)',
  fontSize: 'var(--type-body-xs-size, 11px)',
};

const abstractStyle: React.CSSProperties = {
  color: 'var(--text-secondary, #535366)',
  lineHeight: '1.5',
};

const BrowserContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;

  @media (max-width: 567px) {
    flex-direction: column;
  }
`;

const ListPane = styled.div`
  flex: 0 0 45%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid var(--brand-colors-neutral-400, #dddde2);
  position: relative;

  @media (max-width: 567px) {
    flex: 0 0 50%;
    border-right: none;
    border-bottom: 1px solid var(--brand-colors-neutral-400, #dddde2);
  }
`;

const ListScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const ListItem = styled.button`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs-8, 8px);
  width: 100%;
  padding: var(--spacing-xs-8, 8px) var(--spacing-xs-12, 12px);
  border: none;
  border-left: 3px solid transparent;
  border-bottom: 1px solid var(--brand-colors-neutral-200, #f0f0f3);
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  &[data-selected] {
    background: var(--background-light-blue, #eef2f9);
    border-left-color: var(--primary-brand, #0645b1);
  }

  &:hover:not([data-selected]) {
    background: var(--brand-colors-neutral-200, #f0f0f3);
    border-left-color: var(--brand-colors-neutral-500, #b3b3be);
  }
`;

const ListItemNumber = styled.div`
  flex-shrink: 0;
  width: 20px;
  padding-top: 1px;
`;

const ListItemContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ListItemTitle = styled.div`
  color: var(--text-primary, #141413);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const PreviewPane = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: var(--spacing-xs-16, 16px);

  @media (max-width: 567px) {
    flex: 1;
    min-height: 120px;
  }
`;

const DetailWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
`;

const DetailThumbnail = styled.div`
  width: 100%;
  max-width: 240px;
  aspect-ratio: 8.5 / 11;
  border: 1px solid var(--stroke-beige-light, #ccc9bc);
  background: var(--background-white, #ffffff);
  overflow: hidden;
  border-radius: var(--border-radius-radius-sm);
  margin-bottom: var(--spacing-xs-8, 8px);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const DetailMeta = styled.div`
  display: flex;
  gap: var(--spacing-xs-12, 12px);
`;

const AbstractSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
  margin-top: var(--spacing-xs-8, 8px);
  padding-top: var(--spacing-xs-8, 8px);
  border-top: 1px solid var(--brand-colors-neutral-200, #f0f0f3);
`;
